import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  AI_ASSISTANT_BLUE,
  CONVERSATION_LIMIT_MESSAGE,
  CONVERSATION_WARN_MESSAGE,
} from "./constants";

/**
 * From 97 messages: notice with a New Conversation action (stays until dismissed,
 * since a prompt + reply usually moves the count by two).
 * 48–49 once dismissed: compact remaining-message indicator.
 * 100: input blocked, prominent Start New Conversation (drafts can move along).
 */
export default function AIConversationLimitBanner({ limit, hasDrafts, onNewConversation, disabled }) {
  const [carryDrafts, setCarryDrafts] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const warn = Boolean(limit?.warn);
  useEffect(() => {
    if (!warn) setDismissed(false);
  }, [warn]);
  if (!limit) return null;

  if (limit.limitReached) {
    return (
      <Box
        role="status"
        sx={{
          mx: 1.5,
          mb: 1,
          p: 1.25,
          borderRadius: 2,
          border: "1px solid rgba(217,119,6,0.4)",
          bgcolor: "#fffbeb",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#92400e" }}>
          {CONVERSATION_LIMIT_MESSAGE}
        </Typography>
        {hasDrafts ? (
          <FormControlLabel
            sx={{ mt: 0.25, "& .MuiFormControlLabel-label": { fontSize: 12, color: "#78350f" } }}
            control={
              <Checkbox
                size="small"
                checked={carryDrafts}
                onChange={(e) => setCarryDrafts(e.target.checked)}
              />
            }
            label="Bring my unapproved drafts to the new conversation"
          />
        ) : null}
        <Button
          fullWidth
          variant="contained"
          disableElevation
          disabled={disabled}
          onClick={() => onNewConversation({ carryDrafts: hasDrafts && carryDrafts })}
          sx={{
            mt: 0.75,
            textTransform: "none",
            fontWeight: 700,
            color: "#fff !important",
            bgcolor: `${AI_ASSISTANT_BLUE} !important`,
          }}
        >
          Start New Conversation
        </Button>
        <Typography sx={{ mt: 0.75, fontSize: 11, color: "#92400e" }}>
          Your text in the message box is kept. Draft cards here can still be edited and approved.
        </Typography>
      </Box>
    );
  }

  if (!limit.warn) return null;

  if (!dismissed) {
    return (
      <Stack
        role="status"
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mx: 1.5, mb: 1, px: 1.25, py: 0.9, borderRadius: 2, bgcolor: "#eff6ff", border: "1px solid rgba(0,97,254,0.25)" }}
      >
        <Typography sx={{ flex: 1, fontSize: 12.5, color: "#1e3a8a" }}>
          {CONVERSATION_WARN_MESSAGE}{" "}
          <strong>
            {limit.remaining} message{limit.remaining === 1 ? "" : "s"} left.
          </strong>
        </Typography>
        <Button
          size="small"
          disabled={disabled}
          onClick={() => onNewConversation({ carryDrafts: false })}
          sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0, color: `${AI_ASSISTANT_BLUE} !important` }}
        >
          New Conversation
        </Button>
        <IconButton size="small" aria-label="Dismiss" onClick={() => setDismissed(true)} sx={{ color: "#64748b" }}>
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
    );
  }

  return (
    <Typography
      role="status"
      sx={{ mx: 2, mb: 0.5, fontSize: 11.5, fontWeight: 700, color: "#b45309", textAlign: "right" }}
    >
      {limit.remaining} message{limit.remaining === 1 ? "" : "s"} left in this conversation
    </Typography>
  );
}

AIConversationLimitBanner.propTypes = {
  limit: PropTypes.shape({
    count: PropTypes.number,
    remaining: PropTypes.number,
    warn: PropTypes.bool,
    limitReached: PropTypes.bool,
  }),
  hasDrafts: PropTypes.bool,
  onNewConversation: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
